import {
  setAuthTokenResolver,
  setAuthTokenRenewer,
  setAuthInvalidationHandler,
  type AuthInvalidationHandler,
} from "@/apis/http/authHeaders";

/**
 * Connects the React auth provider to the transport layer, which runs outside
 * React and must not be imported by UI code directly (see the architecture
 * boundary in .oxlintrc.json). The provider registers a token source and a
 * session-invalidation handler here; `apiFetch` consumes them.
 */

/** Registers how the transport layer obtains the current bearer access token. */
export function setAccessTokenResolver(resolver: (() => string | null) | null): void {
  setAuthTokenResolver(resolver);
}

/**
 * Registers how the transport layer silently renews the access token after a 401
 * (OIDC `signinSilent`). Resolves to the fresh token, or `null` when renewal fails —
 * in which case `apiFetch` falls back to session invalidation.
 */
export function setAccessTokenRenewer(renewer: (() => Promise<string | null>) | null): void {
  setAuthTokenRenewer(renewer);
}

/**
 * Registers the action taken when the API rejects the session (401, or a
 * disabled-account 403) — clear the local session and return to login. The
 * handler receives the reason, so a revoked account is not sent straight back
 * through sign-in.
 */
export function setSessionInvalidationHandler(handler: AuthInvalidationHandler | null): void {
  setAuthInvalidationHandler(handler);
}
